import React, { useState } from 'react';

import useQuery from '../../../helpers/JIFI/useQuery';

import SupplyChainNav from '../../../components/supply-chain/nav.js';
import ContainerSelect from '../../../components/supply-chain/container-select.js';
import { Col } from '@shadcn/components/ui/col.jsx';
import { Card, CardContent as CardBody } from '@shadcn/components/ui/card.jsx';
import { TableSkeleton } from '@shadcn/components/ui/table.jsx';

const fmtMoney = (minor) => (minor != null ? (minor / 100).toFixed(2) : '-');

// COG_unit breakdown for a container: FOB + cubic-allocated import fee per SKU.
const CogsBreakdown = () => {
  const [containerId, setContainerId] = useState('');

  const [rows, rowsState] = useQuery({
    url: `/cogs/container/${encodeURIComponent(containerId)}`,
    runQuery: !!containerId,
  });

  const data = Array.isArray(rows) ? rows : [];
  const feeTotal = data.reduce((s, r) => s + (r.lineFeeMinorUnits ?? 0), 0);
  const importFeesTotal = data[0]?.importFeesTotalMinorUnits ?? 0;
  const feesReconcile = data.length > 0 && Math.abs(feeTotal - importFeesTotal) < 1;

  return (
    <SupplyChainNav>
      <div>
        <Col className="w-full">
          <Card className="pt-6 mb-10">
            <CardBody>
              <h4 className="mb-3">COG / Unit Breakdown</h4>
              <div className="mb-4" style={{ maxWidth: 520 }}>
                <ContainerSelect value={containerId} onChange={setContainerId} />
              </div>

              {containerId ? (
                <>
                  {data.length > 0 && (
                    <p className={feesReconcile ? 'text-success' : 'text-danger'}>
                      Allocated fees {fmtMoney(feeTotal)} vs import fees total{' '}
                      {fmtMoney(importFeesTotal)} — {feesReconcile ? '✓ reconciles' : '✗ mismatch'}
                    </p>
                  )}
                  <div className="table-responsive">
                    <table className="table table-centered table-nowrap">
                      <thead>
                        <tr>
                          <th>SKU</th>
                          <th className="text-center">FOB</th>
                          <th className="text-center">Unit Cubic</th>
                          <th className="text-center">Qty</th>
                          <th className="text-center">Allocated Fee</th>
                          <th className="text-center">COG / Unit</th>
                        </tr>
                      </thead>
                      {rowsState === 'success' ? (
                        <tbody>
                          {data.length > 0 ? (
                            data.map((r, key) => (
                              <tr key={`${r.sku}-${key}`}>
                                <td className="font-weight-bold">{r.sku}</td>
                                <td className="text-center">{fmtMoney(r.fobMinorUnits)}</td>
                                <td className="text-center">{r.unitCubic ?? '-'}</td>
                                <td className="text-center">{r.qtyOnContainer ?? '-'}</td>
                                <td className="text-center">{fmtMoney(r.lineFeeMinorUnits)}</td>
                                <td className="text-center font-weight-bold">
                                  {fmtMoney(r.cogUnitMinorUnits)}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={6} className="text-center">
                                No COGS computed for this container
                              </td>
                            </tr>
                          )}
                        </tbody>
                      ) : (
                        <TableSkeleton columns={6} perPage={5} centerIndex={[1, 2, 3, 4, 5]} />
                      )}
                    </table>
                  </div>
                </>
              ) : (
                <p className="text-muted-foreground">Search for a container to see its per-SKU landed cost.</p>
              )}
            </CardBody>
          </Card>
        </Col>
      </div>
    </SupplyChainNav>
  );
};

export default CogsBreakdown;
